import React, { useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import api from "../lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

/**
 * VoiceCheckButton — hold-to-record mic button.
 * Records audio via MediaRecorder → uploads to /api/factcheck/voice → returns a fact-check.
 */
export function VoiceCheckButton({ mode = "auto", onResult }) {
    const [recording, setRecording] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const mediaRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);
    const navigate = useNavigate();

    const start = async () => {
        if (recording || processing) return;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mr = new MediaRecorder(stream);
            chunksRef.current = [];
            mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
            mr.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                await upload();
            };
            mr.start();
            mediaRef.current = mr;
            setRecording(true);
            setElapsed(0);
            timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
        } catch (e) {
            toast.error("Microphone access denied");
        }
    };

    const stop = () => {
        if (!recording) return;
        clearInterval(timerRef.current);
        mediaRef.current?.stop();
        setRecording(false);
        setProcessing(true);
    };

    const upload = async () => {
        try {
            const blob = new Blob(chunksRef.current, { type: "audio/webm" });
            if (blob.size < 800) { toast.error("Too short — hold longer"); setProcessing(false); return; }
            const form = new FormData();
            form.append("audio", blob, "recording.webm");
            form.append("mode", mode);
            const { data } = await api.post("/factcheck/voice", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onResult?.(data);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.code === "daily_limit_reached") {
                toast.error("Daily free limit reached");
                navigate("/pricing?reason=limit");
            } else {
                toast.error(typeof detail === "string" ? detail : detail?.message || "Voice check failed");
            }
        } finally {
            setProcessing(false);
        }
    };

    return (
        <div className="inline-flex items-center gap-2" data-testid="voice-check-wrapper">
            <Button
                onClick={recording ? stop : start}
                disabled={processing}
                data-testid={recording ? "voice-stop-button" : "voice-start-button"}
                variant={recording ? "destructive" : "secondary"}
                className={`rounded-full h-10 px-4 gap-2 ${recording ? "animate-pulse" : ""}`}
                aria-label={recording ? "Stop recording" : "Start voice check"}
            >
                {processing ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Transcribing…</>
                ) : recording ? (
                    <><Square className="h-4 w-4" fill="currentColor" /> Stop · {elapsed}s</>
                ) : (
                    <><Mic className="h-4 w-4" /> Speak the claim</>
                )}
            </Button>
            <AnimatePresence>
                {recording && (
                    <motion.span
                        initial={{ opacity: 0, x: -4 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0 }}
                        className="text-xs text-red-400 font-semibold flex items-center gap-1"
                    >
                        <span className="w-2 h-2 rounded-full bg-red-500 pulse-dot" /> Recording
                    </motion.span>
                )}
            </AnimatePresence>
        </div>
    );
}
